
import { bookData } from '../../../mocks/en/bookData';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

export default function About() {
  const bookReveal = useScrollReveal({ threshold: 0.2 });
  const contentReveal = useScrollReveal({ threshold: 0.2 });
  const statsReveal = useScrollReveal({ threshold: 0.3 });

  return (
    <section id="about" className="py-24 bg-gradient-to-b from-stone-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-5 gap-4 items-center">
          {/* Left side - Book image with fade-in from left */}
          <div className="lg:col-span-2" ref={bookReveal.ref}>
            <div className={`relative ${bookReveal.isVisible ? 'opacity-100 animate-fade-in-left' : 'opacity-0'}`}>
              <div className="bg-white rounded-2xl shadow-xl p-8 hover-lift">
                <div className="absolute top-4 left-4 bg-white px-4 py-2 rounded-full shadow-md text-sm font-semibold text-gray-700">
                  PDF Format
                </div>
                <img 
                  src={bookData.coverImage} 
                  alt="The Four Gates Within book cover - astrology and self-discovery guide in PDF format"
                  className="w-full rounded-lg"
                  loading="lazy"
                />
                <div className="absolute bottom-8 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6 rounded-b-lg">
                  <p className="text-white text-center font-semibold">
                    {bookData.pages} pages | {bookData.chapters} chapters
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Content with fade-in from right */}
          <div className="lg:col-span-3 space-y-6" ref={contentReveal.ref}>
            <div className={`inline-block ${contentReveal.isVisible ? 'opacity-100 animate-fade-in-right stagger-1' : 'opacity-0'}`}>
              <span className="text-purple-600 font-semibold text-sm tracking-wider uppercase">
                ABOUT THE BOOK
              </span>
            </div>
            
            <h2 className={`font-serif text-5xl lg:text-6xl font-bold text-gray-900 leading-tight ${contentReveal.isVisible ? 'opacity-100 animate-fade-in-right stagger-2' : 'opacity-0'}`}>
              Your Personal Map<br />
              To Authentic Living
            </h2>
            
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p className={`font-medium text-gray-900 ${contentReveal.isVisible ? 'opacity-100 animate-fade-in-right stagger-3' : 'opacity-0'}`}>
                This is not just another book you read and forget.
              </p>
              <p className={`${contentReveal.isVisible ? 'opacity-100 animate-fade-in-right stagger-4' : 'opacity-0'}`}>
                It's a practical journey through 4 elements that helps you really feel the change: in thoughts, emotions, relationships, and sense of life direction.
              </p>
              
              {/* Elements with icons - bounce-in animation */}
              <div className="space-y-4 pt-2">
                <div className={`flex items-start gap-4 ${contentReveal.isVisible ? 'opacity-100 animate-bounce-in stagger-5' : 'opacity-0'}`}>
                  <div className="text-3xl flex-shrink-0">🔥</div>
                  <div>
                    <span className="font-semibold text-gray-900">FIRE</span>
                    <span className="text-gray-700"> – when lacking energy, courage, action</span>
                  </div>
                </div>
                
                <div className={`flex items-start gap-4 ${contentReveal.isVisible ? 'opacity-100 animate-bounce-in stagger-6' : 'opacity-0'}`}>
                  <div className="text-3xl flex-shrink-0">🌬️</div>
                  <div>
                    <span className="font-semibold text-gray-900">AIR</span>
                    <span className="text-gray-700"> – when too many thoughts, unclear what you want</span>
                  </div>
                </div>
                
                <div className={`flex items-start gap-4 ${contentReveal.isVisible ? 'opacity-100 animate-bounce-in stagger-7' : 'opacity-0'}`}>
                  <div className="text-3xl flex-shrink-0">🌊</div>
                  <div>
                    <span className="font-semibold text-gray-900">WATER</span>
                    <span className="text-gray-700"> – when lacking connection, gentleness, emotions</span>
                  </div>
                </div>
                
                <div className={`flex items-start gap-4 ${contentReveal.isVisible ? 'opacity-100 animate-bounce-in stagger-8' : 'opacity-0'}`}>
                  <div className="text-3xl flex-shrink-0">🌿</div>
                  <div>
                    <span className="font-semibold text-gray-900">EARTH</span>
                    <span className="text-gray-700"> – when lacking stability, routine, foundation</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats with hover lift and shimmer effect */}
            <div className="grid grid-cols-2 gap-6 pt-6" ref={statsReveal.ref}>
              <div className={`bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl hover-lift relative overflow-hidden ${statsReveal.isVisible ? 'opacity-100 animate-scale-in stagger-1' : 'opacity-0'}`}>
                <div className="absolute inset-0 animate-shimmer"></div>
                <div className="relative z-10">
                  <div className="text-4xl font-bold text-purple-600 mb-2">{bookData.chapters}</div>
                  <div className="text-gray-700 font-medium">Chapters</div>
                  <div className="text-sm text-gray-600 mt-1">Structured content</div>
                </div>
              </div>
              <div className={`bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-xl hover-lift relative overflow-hidden ${statsReveal.isVisible ? 'opacity-100 animate-scale-in stagger-2' : 'opacity-0'}`}>
                <div className="absolute inset-0 animate-shimmer"></div>
                <div className="relative z-10">
                  <div className="text-4xl font-bold text-amber-600 mb-2">{bookData.exercises}+</div>
                  <div className="text-gray-700 font-medium">Exercises</div>
                  <div className="text-sm text-gray-600 mt-1">Practical tools</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
